import UserRoles from "../model/enums/UserRole";
import { User } from "../model/User";
import APIEndPoints from "../utils/APIEndPoints";
import { IApiClient } from "./ApiClient";
import SETTINGS from "../globals";

export interface IAuthManager {
  getToken(): string | undefined;
  setToken(token: string): void;
  signOut(apiClient: IApiClient): Promise<void>;
  getUserInfo(): User | undefined;
  setUserInfo(info: User | undefined): void;
  getUserID(): number | undefined;
  getUserRole(): UserRoles | undefined;
  getUserName(): string | undefined;
  setUserName(username: string): void;
  setBoardMember(is_board_member: boolean): void;
  isAdmin(): boolean;
  isVisitor(): boolean;
  isBoardMember(): boolean;
  isModerator(): boolean;
  isSpecialist(): boolean;
  launchSSOAuthenticationChallenge(): void;
}

export class AuthManager implements IAuthManager {
  private token: string | undefined;
  private userInfo: User | undefined;
  private userID: number | undefined;
  private userRole: UserRoles | undefined;
  private userName: string | undefined;
  private boardMember = false;

  constructor() {
    // try to read the token from the local storage
    const lsToken = localStorage.getItem("session_token");
    this.setToken(lsToken);
  }

  getUserName(): string | undefined {
    return this.userName;
  }

  setUserName(username: string | undefined): void {
    this.userName = username;
  }

  getToken(): string | undefined {
    return this.token;
  }

  setToken(token: string | null | undefined): boolean {
    if (token === "" || token === null || token === undefined) {
      this.token = undefined;
      // remove the token from local storage
      localStorage.removeItem("session_token");
      // delete all user data
      this.setUserInfo(undefined);
      this.userID = undefined;
      this.userRole = undefined;
      this.boardMember = false;
    } else {
      // process the token (1) make sure is valid; (2) get userID and Role
      const arrayToken = token.split(".");
      // check formal structure of the token
      if (!Array.isArray(arrayToken) || arrayToken.length !== 3) {
        return false;
      }

      // extract and validate token information
      const tokenPayload = JSON.parse(atob(arrayToken[1]));
      const now = new Date().getTime() / 1000;
      const auxUserID = parseInt(tokenPayload.sub);
      if (
        isNaN(auxUserID) ||
        auxUserID <= 0 ||
        !this.isValidUserRole(tokenPayload.role) ||
        tokenPayload.exp < now
      ) {
        return false;
      }

      // checks complete => save the value in the local storage
      this.userID = auxUserID;
      this.userRole = tokenPayload.role;
      this.token = token;
      localStorage.setItem("session_token", token);
    }
    return true;
  }

  signOut(apiClient: IApiClient): Promise<void> {
    return apiClient
      .delete<void>({ url: APIEndPoints.SIGNOUT, requiresToken: true })
      .finally(() => {
        this.setToken("");
      });
  }

  getUserInfo(): User | undefined {
    return this.userInfo;
  }

  setUserInfo(info: User | undefined): void {
    this.userInfo = info;
  }

  getUserID(): number | undefined {
    return this.userID;
  }

  getUserRole(): UserRoles | undefined {
    return this.userRole;
  }

  setBoardMember(is_board_member: boolean): void {
    this.boardMember = is_board_member;
  }

  isAdmin(): boolean {
    return this.userRole === UserRoles.ADMIN;
  }

  isBoardMember(): boolean {
    return this.boardMember;
  }

  isModerator(): boolean {
    return this.userRole === UserRoles.MODERATOR;
  }

  isSpecialist(): boolean {
    return this.userRole === UserRoles.SPECIALIST;
  }

  isVisitor(): boolean {
    return this.userRole === UserRoles.VISITOR;
  }

  async launchSSOAuthenticationChallenge() {
    const state = this.generateRandomString(32);
    const codeVerifier = this.generateRandomString(64);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const nonce = this.generateRandomString(32);

    sessionStorage.setItem("oauth_state", state);
    sessionStorage.setItem("oauth_code_verifier", codeVerifier);

    const redirectUri = process.env.REACT_APP_OAUTH_RETURN_URL!;
    //window.location.origin + "/auth/callback";

    const params = new URLSearchParams({
      response_type: "code",
      client_id: process.env.REACT_APP_OAUTH_KEY!,
      redirect_uri: redirectUri,
      scope: "openid profile",
      state,
      nonce,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });

    window.location.href =
      SETTINGS.API_ROOT +
      APIEndPoints.OAUTH.AUTHORIZE +
      "?" +
      params.toString();
  }

  private isValidUserRole(role: String): boolean {
    if (role === UserRoles.ADMIN) return true;
    if (role === UserRoles.MODERATOR) return true;
    if (role === UserRoles.SPECIALIST) return true;
    if (role === UserRoles.VISITOR) return true;
    return false;
  }

  // --------------------------------- Code + Challenge Generator ----------------------------------

  private generateRandomString(byteLength: number): string {
    const array = new Uint8Array(byteLength);
    window.crypto.getRandomValues(array);
    return this.base64UrlEncode(array);
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const data = new TextEncoder().encode(verifier);
    const hash = await window.crypto.subtle.digest("SHA-256", data);
    return this.base64UrlEncode(new Uint8Array(hash));
  }

  private base64UrlEncode(buffer: Uint8Array): string {
    return btoa(String.fromCharCode(...Array.from(buffer)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  // -----------------------------------------------------------------------------------------------
}
